import { isDefined } from "@clipboard-health/util-ts";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { useArrivalTimePoints } from "@src/appV2/redesign/ArrivalTime/useArrivalTimePoints";
import {
  SHIFT_DISCOVERY_SHIFT_AUCTION_PLACE_BID_MODAL_PATH,
  SHIFT_DISCOVERY_SHIFT_CONFIRM_BOOKING_MODAL_PATH,
} from "@src/appV2/redesign/ShiftDiscovery/paths";
import { useShiftModalsDataContext } from "@src/appV2/redesign/ShiftDiscovery/useShiftModalsDataContext";
import type { UseModalState } from "@src/appV2/redesign/system/ui-components";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { parseISO } from "date-fns";
import { useHistory } from "react-router-dom";

import { useArrivalTimeConfig } from "../../../ArrivalTime/useArrivalTimeConfig";
import { convertModalShiftDataToUseBookShiftParams } from "../../Booking/convertModalShiftDataToUseBookShiftParams";
import { PreBookingCheck, useBookShift } from "../../Booking/useBookShift";
import { type ModalShiftData } from "../../types";
import { ArrivalTimeBottomSheet } from "./Sheet";

interface ArrivalTimeBottomSheetContainerProps {
  readonly modalState: UseModalState;
  readonly shift: ModalShiftData;
  readonly rateNegotiationId: string | undefined;
  readonly isWorkerPlacingABid: boolean;
}

export function ArrivalTimeBottomSheetContainer(props: ArrivalTimeBottomSheetContainerProps) {
  const { modalState, shift, rateNegotiationId, isWorkerPlacingABid } = props;

  const history = useHistory();

  const {
    id: shiftId,
    workplace,
    attributes: { start },
  } = shift;

  const {
    attributes: {
      location: { timezone: workplaceTimezone },
    },
  } = workplace;

  const shiftStartDate = parseISO(start);

  const { userId: workerId } = useDefinedWorker();

  const { navigateToModal, computedArrivalData, resolvedShiftQualification } =
    useShiftModalsDataContext();

  const { data: arrivalTimePoints, isLoading: isLoadingArrivalTimePoints } = useArrivalTimePoints({
    workerId,
    qualification: resolvedShiftQualification,
  });

  const { latePointsDeduction, onTimePointsReward } = arrivalTimePoints ?? {};

  const { gracePeriodInMinutesForDriveETA } = useArrivalTimeConfig();

  const targetArrivalTime = computedArrivalData?.targetArrivalTime;
  const departureTime = computedArrivalData?.departureTime;

  useLogEffect(
    APP_V2_USER_EVENTS.SHIFT_ARRIVAL_TIME_CONFIRMATION_MODAL_VIEWED,
    { shiftId, targetArrivalTime, workerId },
    { enabled: modalState.modalIsOpen && isDefined(targetArrivalTime) }
  );

  const bookShiftParams = convertModalShiftDataToUseBookShiftParams({
    shift,
    navigateToModal,
    onBook: () => {
      navigateToModal(SHIFT_DISCOVERY_SHIFT_CONFIRM_BOOKING_MODAL_PATH, {
        pathParams: { shiftId },
      });
    },
    computedArrivalData,
    rateNegotiationId,
  });

  const {
    attemptBookingShift,
    isBooking,
    isLoading: isLoadingUseBookShift,
  } = useBookShift(bookShiftParams);

  return (
    <ArrivalTimeBottomSheet
      modalState={modalState}
      targetDatetime={targetArrivalTime ?? shiftStartDate}
      workplaceTimezone={workplaceTimezone ?? "America/Los_Angeles"}
      shiftStartDate={shiftStartDate}
      latePointsDeduction={latePointsDeduction}
      departureDatetime={departureTime}
      gracePeriodInMinutesForDriveETA={gracePeriodInMinutesForDriveETA}
      isLoadingPoints={isLoadingArrivalTimePoints}
      isLoadingBooking={isLoadingUseBookShift || isBooking}
      isWorkerPlacingABid={isWorkerPlacingABid}
      onTimePointsReward={onTimePointsReward}
      onBack={() => {
        history.goBack();
      }}
      onConfirm={async () => {
        logEvent(APP_V2_USER_EVENTS.SHIFT_ARRIVAL_TIME_CONFIRMED, {
          shiftId,
          workerId,
          targetArrivalTime,
        });
        if (isWorkerPlacingABid) {
          navigateToModal(SHIFT_DISCOVERY_SHIFT_AUCTION_PLACE_BID_MODAL_PATH, {
            pathParams: { shiftId },
            clearExistingSearchParams: true,
          });
        } else {
          await attemptBookingShift({
            skipChecks: [PreBookingCheck.ARRIVAL_TIME, PreBookingCheck.EDUCATE_NEW_WORKER],
          });
        }
      }}
    />
  );
}
